import { useStore } from "@/stores/useStore";
import { useCallback, useMemo, useState } from "react";
import { CustomerUpdateFormValues } from "@/components/CustomerUpdate/CustomerUpdateForm";
import { customerUpdate as customerUpdateRepository } from "@/repositories/customerUpdates";
import { useInvalidateCustomerUpdates } from "@/hooks/queries/usePaginatedCustomerUpdates";
import { useCustomerUpdateById } from "@/hooks/queries/useCustomerUpdateById";
import { DEFAULT_CUSTOMER_UPDATE_SNOOZE_MIN } from "@/lib/const";
import { CustomerUpdateMedium } from "@/types/v2";
import { useInvalidateTotalCustomerUpdatesQuery } from "@/hooks/queries/useTotalCustomerUpdatesQuery";
import { showErrorToast } from "@/lib/toast";

export const useCustomerUpdate = (customerUpdateId: string | undefined) => {
  const shopId = useStore((state) => state.shop?.id);
  const customerUpdateInfo = useStore(
    (state) => state.shop?.shopConfig?.customerUpdates
  );
  const customerUpdates = useStore((state) => state.customerUpdates);
  const myCustomerUpdates = useStore((state) => state.myCustomerUpdates);
  const cards = useStore((state) => state.cards);
  const invalidateCustomerUpdates = useInvalidateCustomerUpdates(shopId);
  const invalidateTotalCustomerUpdates =
    useInvalidateTotalCustomerUpdatesQuery();
  const [customerUpdateSubmitting, setCustomerUpdateSubmitting] =
    useState(false);

  // Try to get customer update from store first
  const storeCustomerUpdate = useMemo(
    () =>
      customerUpdates.find((cu) => cu.id === customerUpdateId) ??
      myCustomerUpdates.find((cu) => cu.id === customerUpdateId),
    [customerUpdates, customerUpdateId, myCustomerUpdates]
  );

  // Use React Query as fallback when customer update is not in store
  const { data: queriedCustomerUpdate } = useCustomerUpdateById({
    shopId,
    customerUpdateId,
    enabled: !storeCustomerUpdate && !!customerUpdateId,
  });

  // Prefer store data over queried data for better performance
  const customerUpdate = storeCustomerUpdate ?? queriedCustomerUpdate;

  const card = useMemo(
    () => cards.find((c) => c.id === customerUpdate?.cardId),
    [cards, customerUpdate]
  );

  const handleCreateUpdate = useCallback(
    async (cardId: string, values: CustomerUpdateFormValues) => {
      if (!shopId) {
        console.error("Shop ID is missing");
        return;
      }
      const userId = values.userId;
      if (!userId) {
        console.error("User ID is missing");
        return;
      }
      setCustomerUpdateSubmitting(true);
      return customerUpdateRepository
        .createUpdate(shopId, cardId, userId, values.desiredUpdateTime)
        .finally(() => setCustomerUpdateSubmitting(false));
    },
    [shopId]
  );

  const handleScheduleUpdate = useCallback(
    async (values: CustomerUpdateFormValues) => {
      if (!shopId || !customerUpdateId) {
        console.error("Shop ID or Customer Update ID is missing");
        return;
      }
      setCustomerUpdateSubmitting(true);
      if (values.comments && values.comments.length > 0) {
        if (!card?.id) {
          console.error("Card ID is missing");
          return;
        }
        return customerUpdateRepository.completeInitialUpdate(
          shopId,
          card.id,
          customerUpdateId,
          values.desiredUpdateTime,
          values.comments,
          values.updateMedium as CustomerUpdateMedium[],
          values.userId,
          values.isFinal
        );
      }
      return customerUpdateRepository
        .scheduleUpdate(shopId, customerUpdateId, values.desiredUpdateTime)
        .finally(() => setCustomerUpdateSubmitting(false));
    },
    [shopId, customerUpdateId, card]
  );

  const handleSkipUpdate = useCallback(
    (values: CustomerUpdateFormValues) => {
      if (!shopId || !customerUpdateId) {
        console.error("Shop ID or Customer Update ID is missing");
        return;
      }
      setCustomerUpdateSubmitting(true);
      return customerUpdateRepository
        .skipUpdate(
          shopId,
          customerUpdateId,
          values.desiredUpdateTime,
          values.skipReason,
          values.userId,
          values.isFinal
        )
        .finally(() => setCustomerUpdateSubmitting(false));
    },
    [shopId, customerUpdateId, invalidateTotalCustomerUpdates]
  );

  const handleSnoozeUpdate = useCallback(async () => {
    if (!shopId || !customerUpdateId) {
      console.error("Shop ID or Customer Update ID is missing");
      return;
    }
    setCustomerUpdateSubmitting(true);
    return customerUpdateRepository
      .snoozeUpdate(
        shopId,
        customerUpdateId,
        new Date(
          new Date().getTime() +
            (customerUpdateInfo?.defaultSnoozeBufferMin ??
              DEFAULT_CUSTOMER_UPDATE_SNOOZE_MIN) *
              60 *
              1000
        )
      )
      .finally(() => setCustomerUpdateSubmitting(false));
  }, [shopId, customerUpdateId, customerUpdateInfo?.defaultSnoozeBufferMin]);

  const handleCompleteUpdate = useCallback(
    (values: CustomerUpdateFormValues) => {
      if (!shopId) {
        console.error("Shop ID is missing");
        return;
      }

      if (!customerUpdateId) {
        console.error("Customer Update ID is missing");
        return;
      }

      if (!card?.id) {
        console.error("Card ID is missing");
        return;
      }
      setCustomerUpdateSubmitting(true);
      return customerUpdateRepository
        .completeUpdate(
          shopId,
          card.id,
          customerUpdateId,
          values.desiredUpdateTime,
          values.comments || "No Comments Provided",
          values.updateMedium as CustomerUpdateMedium[],
          values.userId
        )
        .finally(() => setCustomerUpdateSubmitting(false));
    },
    [shopId, customerUpdateId, card?.id]
  );

  const handleCancelUpdate = useCallback(
    (values: CustomerUpdateFormValues) => {
      if (!shopId || !customerUpdateId) {
        console.error("Shop ID or Customer Update ID is missing");
        return;
      }
      return customerUpdateRepository.cancelUpdate(
        shopId,
        customerUpdateId,
        values.comments
      );
    },
    [shopId, customerUpdateId]
  );

  const handleSubmit = useCallback(
    async (values: CustomerUpdateFormValues) => {
      try {
        let response;
        if (values.type === "skip") {
          response = await handleSkipUpdate(values);
        } else if (values.type === "new") {
          response = await handleScheduleUpdate(values);
        } else if (values.type === "update") {
          if (values.isFinal) {
            response = await handleCancelUpdate(values);
          } else {
            response = await handleCompleteUpdate(values);
          }
        }
        if (response?.data?.success) {
          invalidateCustomerUpdates();
          invalidateTotalCustomerUpdates();
        }
        return response;
      } catch (err) {
        console.error("Failed to save customer update:", err);
        showErrorToast(
          "Customer Update Failed",
          "We couldn't save this customer update. Please try again."
        );
        throw err;
      }
    },
    [
      handleScheduleUpdate,
      handleSkipUpdate,
      handleCompleteUpdate,
      handleCancelUpdate,
      invalidateCustomerUpdates,
      invalidateTotalCustomerUpdates,
    ]
  );

  const snoozeTimeMin = useMemo(
    () =>
      customerUpdateInfo?.defaultSnoozeBufferMin ??
      DEFAULT_CUSTOMER_UPDATE_SNOOZE_MIN,
    [customerUpdateInfo?.defaultSnoozeBufferMin]
  );

  return {
    customerUpdate,
    card,
    handleSubmit,
    handleCreateUpdate,
    handleSnoozeUpdate,
    snoozeTimeMin,
    customerUpdateSubmitting,
  };
};
